import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { cn } from '@/lib/utils'

export interface ConditionOption {
  id: number
  label: string
}

export interface ConditionGroup {
  key: string
  /** Optional group header (e.g. the parent subtype name for a checklist item). */
  label?: string
  options: ConditionOption[]
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  groups: ConditionGroup[]
  selectedId: number | null
  onSelect: (id: number | null) => void
}

/** Dialog that lets the user pick one element (or clear the selection) for a subtype condition. */
export function ConditionPickerDialog({ open, onOpenChange, title, groups, selectedId, onSelect }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const hasOptions = groups.some(g => g.options.length > 0)

  const select = (id: number | null) => {
    onSelect(id)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="flex flex-col gap-1">
            <Row label={t('tabSubtypes.condition.none')} selected={selectedId === null} onClick={() => select(null)} />
            {!hasOptions && (
              <p className="px-2 py-4 text-sm text-muted-foreground">{t('tabSubtypes.condition.empty')}</p>
            )}
            {groups.map(g =>
              g.options.length === 0 ? null : (
                <div key={g.key} className="flex flex-col gap-1">
                  {g.label && (
                    <p className="px-2 pt-2 text-xs font-medium uppercase text-muted-foreground">{g.label}</p>
                  )}
                  {g.options.map(o => (
                    <Row key={o.id} label={o.label} selected={selectedId === o.id} onClick={() => select(o.id)} />
                  ))}
                </div>
              ),
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

function Row({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-between rounded-md px-2 py-2 text-left text-sm hover:bg-accent',
        selected && 'bg-accent',
      )}
    >
      <span className="truncate">{label}</span>
      {selected && <Check className="size-4 shrink-0" />}
    </button>
  )
}
